import config from "../../config/environment";

/**
 * Enhanced token storage utility with improved security and expiration handling
 *
 * Features:
 * - Token encryption using a more robust approach
 * - Token expiration validation
 * - Automatic handling of token expiration
 * - Uses environment configuration
 */

interface StoredToken {
  value: string;
  expiry: number; // Timestamp when the token expires
}

// More secure encryption/decryption for token storage
const encrypt = (value: string): string => {
  try {
    // In a production environment, consider using the Web Crypto API
    // for stronger encryption. For now, we use a more robust encoding pattern
    // than plain base64 to deter casual inspection
    const buffer = new TextEncoder().encode(value);
    return Array.from(buffer)
      .map((byte) => String.fromCharCode(byte ^ 0x41)) // Simple XOR with 'A'
      .join("");
  } catch (error) {
    console.error("Error encrypting token:", error);
    return value;
  }
};

const decrypt = (value: string): string => {
  try {
    // Reverse the encryption process
    const buffer = new Uint8Array(
      value.split("").map((char) => char.charCodeAt(0) ^ 0x41),
    );
    return new TextDecoder().decode(buffer);
  } catch (error) {
    console.error("Error decrypting token:", error);
    return value;
  }
};

// Helper function to store a token with expiration
const storeToken = (
  key: string,
  token: string,
  expiryDuration: number,
): void => {
  const item: StoredToken = {
    value: token,
    expiry: Date.now() + expiryDuration,
  };
  localStorage.setItem(key, encrypt(JSON.stringify(item)));
};

// Helper function to retrieve a token and check expiration
const retrieveToken = (key: string): string | null => {
  const storedItem = localStorage.getItem(key);
  if (!storedItem) return null;

  try {
    const item: StoredToken = JSON.parse(decrypt(storedItem));

    // Check if the token has expired
    if (Date.now() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch (error) {
    console.error("Error retrieving token:", error);
    localStorage.removeItem(key);
    return null;
  }
};

// Calculate remaining time (in ms) until token expiry
const getTokenRemainingTime = (key: string): number | null => {
  const storedItem = localStorage.getItem(key);
  if (!storedItem) return null;

  try {
    const item: StoredToken = JSON.parse(decrypt(storedItem));
    const remainingTime = item.expiry - Date.now();
    return remainingTime > 0 ? remainingTime : null;
  } catch (error) {
    console.error("Error calculating token remaining time:", error);
    return null;
  }
};

export const tokenStorage = {
  // Auth token methods
  getAuthToken: (): string | null => {
    return retrieveToken(config.auth.tokenKey);
  },

  setAuthToken: (
    token: string,
    expiryDuration = config.auth.tokenExpiry,
  ): void => {
    storeToken(config.auth.tokenKey, token, expiryDuration);
  },

  removeAuthToken: (): void => {
    localStorage.removeItem(config.auth.tokenKey);
  },

  // Refresh token methods
  getRefreshToken: (): string | null => {
    // Refresh tokens typically have longer lifetime than auth tokens
    return retrieveToken(config.auth.refreshTokenKey);
  },

  setRefreshToken: (
    token: string,
    expiryDuration = config.auth.tokenExpiry * 24,
  ): void => {
    // Refresh tokens typically last longer (24x the regular token in this case)
    storeToken(config.auth.refreshTokenKey, token, expiryDuration);
  },

  removeRefreshToken: (): void => {
    localStorage.removeItem(config.auth.refreshTokenKey);
  },

  // Check if auth token is about to expire (30 seconds threshold)
  isAuthTokenExpiringSoon: (thresholdMs = 30000): boolean => {
    const remainingTime = getTokenRemainingTime(config.auth.tokenKey);
    return remainingTime !== null && remainingTime < thresholdMs;
  },

  // Get the remaining time for auth token in milliseconds
  getAuthTokenRemainingTime: (): number | null => {
    return getTokenRemainingTime(config.auth.tokenKey);
  },

  // Clear all auth tokens
  clearTokens: (): void => {
    tokenStorage.removeAuthToken();
    tokenStorage.removeRefreshToken();
  },

  // Get authorization header
  getAuthHeader: (): Record<string, string> => {
    const token = tokenStorage.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  // Get token data including expiration
  getTokenData: (): { token: string; expiresAt: Date } | null => {
    const storedItem = localStorage.getItem(config.auth.tokenKey);
    if (!storedItem) return null;

    try {
      const item: StoredToken = JSON.parse(decrypt(storedItem));

      // Check if the token has expired
      if (Date.now() > item.expiry) {
        localStorage.removeItem(config.auth.tokenKey);
        return null;
      }

      return {
        token: item.value,
        expiresAt: new Date(item.expiry),
      };
    } catch (error) {
      console.error("Error retrieving token data:", error);
      return null;
    }
  },
};
